'use client'

import Image from 'next/image'
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'

import { useI18n } from './i18n'

const SystemSupportNotificationPopover = lazy(() => import('./SystemSupportNotificationPopover.jsx'))
const REFRESH_EVENT = 'ql7:system-support:refresh'
const MIN_REFRESH_MS = 4 * 60 * 1000
const MAX_SCHEDULE_DELAY_MS = 7 * 24 * 60 * 60 * 1000
const MIN_SCHEDULE_DELAY_MS = 30 * 1000
const TMA_BOOTSTRAP_RETRY_MS = [500, 1600]

function detectTelegramMiniApp() {
  try {
    const initData = String(window?.Telegram?.WebApp?.initData || '').trim()
    if (initData && initData.includes('hash=')) return true
    const hash = String(window?.location?.hash || '')
    return hash.includes('tgWebAppData=') || hash.includes('tgwebappdata=')
  } catch {
    return false
  }
}

function Bell() {
  return (
    <span className="topbarMetaGiftBell ql7-system-support-bell" aria-hidden="true">
      <svg className="topbarMetaGiftBellSvg" viewBox="0 0 48 48" focusable="false">
        <defs>
          <radialGradient id="system-support-bell-glow" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#fff7ed" stopOpacity="1" />
            <stop offset="38%" stopColor="#ff3b30" stopOpacity=".92" />
            <stop offset="100%" stopColor="#7f1d1d" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="system-support-bell-fill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff7b72" />
            <stop offset="52%" stopColor="#ef4444" />
            <stop offset="100%" stopColor="#991b1b" />
          </linearGradient>
          <filter id="system-support-bell-shadow" x="-70%" y="-70%" width="240%" height="240%">
            <feDropShadow dx="0" dy="0" stdDeviation="1.35" floodColor="#fecaca" floodOpacity="1" />
            <feDropShadow dx="0" dy="0" stdDeviation="3.4" floodColor="#ef4444" floodOpacity=".76" />
          </filter>
        </defs>
        <circle className="topbarMetaGiftBellAura" cx="24" cy="24" r="17" fill="url(#system-support-bell-glow)" />
        <g className="topbarMetaGiftBellBody" filter="url(#system-support-bell-shadow)">
          <path className="ql7-system-support-bell-stroke" d="M18.5 18.7c.55-4.1 2.95-6.85 5.5-6.85s4.95 2.75 5.5 6.85l.64 5.22c.2 1.66.82 3.08 1.86 4.25l1.2 1.36H14.8l1.2-1.36c1.04-1.17 1.66-2.59 1.86-4.25l.64-5.22Z" />
          <path className="ql7-system-support-bell-stroke" d="M20.7 32.05c.62 1.82 1.76 2.94 3.3 2.94s2.68-1.12 3.3-2.94" />
          <path className="topbarMetaGiftBellClapper" d="M22.1 29.45h3.8c-.15 1.16-.82 1.9-1.9 1.9s-1.75-.74-1.9-1.9Z" />
          <path className="topbarMetaGiftBellSpark" d="M24 9.3v-3" />
        </g>
        <g className="topbarMetaGiftBellParticles">
          <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--1" cx="12" cy="16" r="1.35" />
          <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--2" cx="36" cy="15" r="1.2" />
          <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--3" cx="10" cy="28" r="1.05" />
          <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--4" cx="38" cy="29" r="1" />
          <path className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--5" d="M8 21h4" />
          <path className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--6" d="M36 22h4" />
        </g>
      </svg>
    </span>
  )
}

export default function SystemSupportNotificationHost() {
  const { t } = useI18n()
  const [unreadCount, setUnreadCount] = useState(0)
  const [open, setOpen] = useState(false)
  const inFlightRef = useRef(null)
  const lastRefreshRef = useRef(0)
  const nextTimerRef = useRef(0)
  const tmaRetryTimerRef = useRef(0)
  const tmaRetryAttemptRef = useRef(0)

  const clearNextTimer = useCallback(() => {
    if (nextTimerRef.current) window.clearTimeout(nextTimerRef.current)
    nextTimerRef.current = 0
  }, [])

  const clearTmaRetryTimer = useCallback(() => {
    if (tmaRetryTimerRef.current) window.clearTimeout(tmaRetryTimerRef.current)
    tmaRetryTimerRef.current = 0
  }, [])

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (typeof window === 'undefined') return null
    const now = Date.now()
    if (!force && now - lastRefreshRef.current < MIN_REFRESH_MS) return null
    if (inFlightRef.current) return inFlightRef.current

    const request = fetch('/api/system-support/notifications?summary=1', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
    }).then(async (response) => {
      if (response.status === 401) {
        setUnreadCount(0)
        if (detectTelegramMiniApp() && tmaRetryAttemptRef.current < TMA_BOOTSTRAP_RETRY_MS.length && !tmaRetryTimerRef.current) {
          const retryIndex = tmaRetryAttemptRef.current
          tmaRetryAttemptRef.current += 1
          tmaRetryTimerRef.current = window.setTimeout(() => {
            tmaRetryTimerRef.current = 0
            void refresh({ force: true })
          }, TMA_BOOTSTRAP_RETRY_MS[retryIndex])
        }
        return null
      }
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.ok) return null
      lastRefreshRef.current = Date.now()
      clearTmaRetryTimer()
      tmaRetryAttemptRef.current = 0
      setUnreadCount(Math.max(0, Number(data.unreadCount || 0)))
      clearNextTimer()
      const due = Date.parse(String(data.nextCheckAt || ''))
      if (Number.isFinite(due)) {
        const delay = Math.max(MIN_SCHEDULE_DELAY_MS, Math.min(MAX_SCHEDULE_DELAY_MS, due - Date.now()))
        nextTimerRef.current = window.setTimeout(() => {
          nextTimerRef.current = 0
          void refresh({ force: true })
        }, delay)
      }
      return data
    }).catch(() => null).finally(() => {
      inFlightRef.current = null
    })
    inFlightRef.current = request
    return request
  }, [clearNextTimer, clearTmaRetryTimer])

  useEffect(() => {
    let idleId = 0
    let timerId = 0
    const bootstrap = () => { void refresh({ force: true }) }
    if (typeof window.requestIdleCallback === 'function') idleId = window.requestIdleCallback(bootstrap, { timeout: 1400 })
    else timerId = window.setTimeout(bootstrap, 700)

    const onRefresh = () => {
      tmaRetryAttemptRef.current = 0
      clearTmaRetryTimer()
      void refresh({ force: true })
    }
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh() }
    const onLogout = () => {
      setOpen(false)
      setUnreadCount(0)
      clearNextTimer()
      clearTmaRetryTimer()
      tmaRetryAttemptRef.current = 0
    }
    if (detectTelegramMiniApp()) {
      try { window.Telegram?.WebApp?.ready?.() } catch {}
    }
    window.addEventListener(REFRESH_EVENT, onRefresh)
    window.addEventListener('wallet-session:verified', onRefresh)
    window.addEventListener('qcoin:auth-ready', onRefresh)
    window.addEventListener('auth:ok', onRefresh)
    window.addEventListener('auth:logout', onLogout)
    window.addEventListener('online', onRefresh)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      if (idleId && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleId)
      if (timerId) window.clearTimeout(timerId)
      clearNextTimer()
      clearTmaRetryTimer()
      window.removeEventListener(REFRESH_EVENT, onRefresh)
      window.removeEventListener('wallet-session:verified', onRefresh)
      window.removeEventListener('qcoin:auth-ready', onRefresh)
      window.removeEventListener('auth:ok', onRefresh)
      window.removeEventListener('auth:logout', onLogout)
      window.removeEventListener('online', onRefresh)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [clearNextTimer, clearTmaRetryTimer, refresh])

  const closePopover = useCallback(() => {
    setOpen(false)
    window.setTimeout(() => { void refresh({ force: true }) }, 120)
  }, [refresh])

  const launcherVisible = unreadCount > 0 && !open
  return (
    <>
      {launcherVisible && (
        <button
          type="button"
          className="ql7-system-support-launcher"
          aria-label={t('system_support_button_aria')}
          onClick={() => setOpen(true)}
          data-ql7-visual-scope="overlay"
        >
          <Image src="/icons/support.png" alt="" width={58} height={58} draggable={false} />
          <Bell />
          <span className="ql7-system-support-count" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
        </button>
      )}
      {open && (
        <Suspense fallback={null}>
          <SystemSupportNotificationPopover
            onClose={closePopover}
            onUnreadCountChange={setUnreadCount}
          />
        </Suspense>
      )}
    </>
  )
}
